import { callExternalApi } from '../callExternalApi';
import { getSelectedAccountIds } from '../../store/User';
import type { ScheduleSetting } from '../../store/ScheduleSettings';

export type { ScheduleSetting };

const BASE_URL = `${process.env.REACT_APP_API_SERVER_URL}/api/schedule_settings`;

const accountIdsParam = () => {
  const ids = getSelectedAccountIds();
  return ids?.length ? `?account_ids=${ids.join(',')}` : '';
};

export type ScheduleViewPayload = { view_key: string; tab_name: string };

export type ScheduleSettingPayload = {
  name: string;
  schedule_rule: Record<string, unknown>;
  start_date: string;
  end_date?: string | null;
  recipients: string[];
  recipient_portal_user_ids?: number[];
  send_email: boolean;
  share_with_team: boolean;
  include_executive_summary: boolean;
  // The first tab's key. The API derives it from `views` when both are sent; an API that predates `views` reads only this.
  view_key: string;
  views?: ScheduleViewPayload[];
  // Scopes a related inventory schedule to one project (Upstack project UUID); omitted for account-wide views.
  project_id?: string;
};

export const fetchScheduleSettings = () =>
  callExternalApi<ScheduleSetting[]>({
    url: `${BASE_URL}${accountIdsParam()}`,
    method: 'GET',
    headers: { 'content-type': 'application/json' }
  });

export const createScheduleSetting = (payload: ScheduleSettingPayload) =>
  callExternalApi<ScheduleSetting>({
    url: `${BASE_URL}${accountIdsParam()}`,
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    data: JSON.stringify({ schedule_setting: payload })
  });

export type ScheduleSettingPreviewPayload = Pick<ScheduleSettingPayload, 'schedule_rule' | 'end_date'>;

// Past this, a stuck SF Sync lookup reads as unavailable instead of leaving the summary loading.
export const PREVIEW_TIMEOUT_MS = 10_000;

export const previewScheduleSetting = (payload: ScheduleSettingPreviewPayload, signal?: AbortSignal) =>
  callExternalApi<{ run_dates: string[] }>({
    url: `${BASE_URL}/preview`,
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    data: JSON.stringify({ schedule_setting: payload }),
    signal,
    timeout: PREVIEW_TIMEOUT_MS
  });

export const updateScheduleSetting = (id: string, payload: Partial<ScheduleSettingPayload>) =>
  callExternalApi<ScheduleSetting>({
    url: `${BASE_URL}/${id}`,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: JSON.stringify({ schedule_setting: payload })
  });

export const deleteScheduleSetting = (id: string) =>
  callExternalApi<void>({
    url: `${BASE_URL}/${id}`,
    method: 'DELETE',
    headers: { 'content-type': 'application/json' }
  });

export const optOutScheduleSetting = (id: string) =>
  callExternalApi<ScheduleSetting>({
    url: `${BASE_URL}/${id}/opt_out`,
    method: 'PATCH',
    headers: { 'content-type': 'application/json' }
  });

export const optInScheduleSetting = (id: string) =>
  callExternalApi<ScheduleSetting>({
    url: `${BASE_URL}/${id}/opt_in`,
    method: 'PATCH',
    headers: { 'content-type': 'application/json' }
  });

export const resumeScheduleSetting = (id: string) =>
  callExternalApi<ScheduleSetting>({
    url: `${BASE_URL}/${id}/resume`,
    method: 'PATCH',
    headers: { 'content-type': 'application/json' }
  });

export const runScheduleSetting = (id: string) =>
  callExternalApi<{ message: string }>({
    url: `${BASE_URL}/${id}/run`,
    method: 'POST',
    headers: { 'content-type': 'application/json' }
  });
